import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';

import { ConfirmDialog } from '@/components/confirm-dialog';
import { GrowthDashboardModal } from '@/components/growth/growth-dashboard-modal';
import { useAppTheme } from '@/theme/app-theme';

/**
 * The dashboard route. The body mounts with it — no deferral, no animation.
 *
 * Build 12 opened and closed this slowly because the route is a modal whose
 * body was ALSO an RN `<Modal>`: two presentations, two dismissals. Removing
 * the inner one fixed closing outright and still stands
 * (`dashboard-presentation.test.ts`).
 *
 * Opening was then blamed on this 3,000-line tree mounting in front of the
 * slide, and the body was deferred behind `InteractionManager`. Build 14
 * measured it: `dashboard.route.mount` to `dashboard.body.painted` is about
 * TEN MILLISECONDS. Nothing was blocking anything — the slide itself was the
 * jank — so the deferral bought nothing and has gone, along with the slide.
 * Goals and Reflections open instantly as plain modals and feel better; an
 * instant appearance cannot stutter.
 */
export default function DashboardScreen() {
  const router = useRouter();
  const { colors } = useAppTheme();

  const closeDashboard = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.replace('/profile');
  }, [router]);

  return (
    <View
      style={[
        styles.screen,
        { backgroundColor: colors.background },
      ]}>
      <GrowthDashboardModal
        visible
        onClose={closeDashboard}
      />

      {/*
        This route is `presentation: 'transparentModal'` — on iOS a real
        UIViewController presentation from the ROOT controller. The dialog host
        at the root presents from that same controller, which is already busy,
        so UIKit refused it and every confirm()/notify() raised in here showed
        nothing: goal delete and reflection delete did nothing at all, and the
        error notices were invisible.

        Mounted inside the route, the dialog presents from THIS route's
        controller, which is presenting nothing. The root host stands down
        while this one exists (host stack, `state/confirm-store.ts`).
      */}
      <ConfirmDialog />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
  },
});
